import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import HeroBanner from '../components/HeroBanner';
import { Filter, Grid, List, Image as ImageIcon } from 'lucide-react';

interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  images: string[];
  colors?: string[];
}

interface CategoryCard {
  category: string;
  title: string;
  description: string;
  imageUrl: string;
  hasCustomImage: boolean;
  listingCount: number;
}

const isImageUrl = (url: string): boolean => url.startsWith('http') || url.startsWith('data:');

const getImageUrl = (imagePath: string): string => {
  if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
    return '';
  }

  if (isImageUrl(imagePath)) return imagePath;

  const normalizedPath = imagePath.replace(/\\/g, '/');
  const filename = normalizedPath.split('/').pop();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return `${apiUrl}/api/products/image/${filename}`;
};

const HomePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryCards, setCategoryCards] = useState<CategoryCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') || 'createdAt';
  const section = searchParams.get('section') || '';
  const categoriesSectionRef = useRef<HTMLDivElement | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (sort) params.append('sort', sort);
      if (search || category) params.append('limit', '1000');

      const response = await api.get(`/api/products?${params}`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [search, category, sort]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/api/products/categories/list');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchCategoryCards = useCallback(async () => {
    try {
      const response = await api.get('/api/category-cards');
      setCategoryCards(response.data || []);
    } catch (error) {
      console.error('Error fetching category cards:', error);
      setCategoryCards([]);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCategoryCards();
  }, [fetchProducts, fetchCategories, fetchCategoryCards]);

  useEffect(() => {
    if (loading) return;
    if (section !== 'categories') return;

    categoriesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const params = new URLSearchParams(searchParams);
    params.delete('section');
    setSearchParams(params, { replace: true });
  }, [loading, section, searchParams, setSearchParams]);

  const handleCategoryChange = (newCategory: string) => {
    const params = new URLSearchParams(searchParams);
    if (newCategory) {
      params.set('category', newCategory);
    } else {
      params.delete('category');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleSortChange = (newSort: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSort);
    setSearchParams(params);
  };

  const selectedCategoryCard = categoryCards.find((card) => card.category === category);
  const showCategoryCards = !search && !category;
  const shouldShowProducts = Boolean(search || category);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen transition-colors duration-300 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-pink-200 dark:bg-dark-surface rounded-lg h-64 transition-colors duration-300"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full transition-colors duration-300 bg-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg" style={{ minHeight: '100vh' }}>
      {(!search && !category) && <HeroBanner />}

      <div id="products-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex items-center justify-between gap-2 mb-6 md:mb-8 p-4 md:p-6 rounded-xl border border-pink-200 bg-pink-100 dark:bg-dark-surface dark:border-dark-border">
          <div className="flex items-center gap-2 md:gap-4 overflow-x-auto whitespace-nowrap pr-1">
            <Filter className="h-5 w-5 text-pink-600 dark:text-pink-400 transition-colors duration-300" />
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="min-w-[9rem] sm:min-w-[12rem] border border-pink-300 dark:border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-500 bg-pink-50 dark:bg-dark-hover text-gray-900 dark:text-dark-text transition-colors duration-300"
            >
              <option value="">جميع الفئات</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {category && (


            <select
              value={sort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="min-w-[8rem] border border-pink-300 dark:border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-500 bg-pink-50 dark:bg-dark-hover text-gray-900 dark:text-dark-text transition-colors duration-300"
            >
              <option value="createdAt">الأحدث</option>
              <option value="price">السعر: من الأقل إلى الأعلى</option>
              <option value="price-desc">السعر: من الأعلى إلى الأقل</option>
            </select>
            )}
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-pink-50 dark:hover:bg-pink-900/20'
              }`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === 'list'
                  ? 'bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-pink-50 dark:hover:bg-pink-900/20'
              }`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>

        {showCategoryCards && (
          <div id="categories-section" ref={categoriesSectionRef} className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-4 transition-colors duration-300">
              تصفح حسب القسم
            </h2>
            {categoryCards.length === 0 ? (
              <div className="text-center py-10 p-6 rounded-xl bg-white/80 dark:bg-dark-surface/80 backdrop-blur-sm">
                <p className="text-gray-500 dark:text-dark-textSecondary transition-colors duration-300">
                  لا توجد أقسام متاحة حالياً.
                </p>
              </div>
            ) : (
              <div className={`gap-4 ${
                viewMode === 'grid'
                  ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                  : 'flex flex-col'
              }`}>
                {categoryCards.map((card) => (
                  <button
                    key={card.category}
                    type="button"
                    onClick={() => handleCategoryChange(card.category)}
                    className={`text-right rounded-xl border border-pink-200 dark:border-dark-border bg-white/90 dark:bg-dark-surface overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${
                      viewMode === 'grid' ? '' : 'flex items-center gap-3 p-3'
                    }`}
                  >
                    <div className={`bg-pink-50 dark:bg-dark-hover ${
                      viewMode === 'grid'
                        ? 'aspect-[3/4] w-full'
                        : 'aspect-[3/4] w-24 sm:w-28 rounded-lg overflow-hidden shrink-0'
                    }`}>
                      {card.imageUrl ? (
                        <img
                          src={getImageUrl(card.imageUrl)}
                          alt={card.title}
                          className="w-full h-full object-cover bg-pink-100 dark:bg-dark-hover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-dark-textSecondary">
                          <ImageIcon className="h-5 w-5 ml-2" />
                          بدون صورة
                        </div>
                      )}
                    </div>
                    <div className={viewMode === 'grid' ? 'p-3' : 'flex-1 min-w-0'}>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text line-clamp-1">{card.title}</h3>
                      <p className={`text-xs text-gray-600 dark:text-dark-textSecondary mt-1 line-clamp-2 ${
                        viewMode === 'grid' ? 'min-h-[2rem]' : ''
                      }`}>
                        {card.description || ' '}
                      </p>
                      <span className="inline-block mt-2 text-xs px-2 py-1 rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                        {card.listingCount} منتج
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {search && (
          <div className="mb-6 p-4 rounded-xl bg-white/80 dark:bg-dark-surface/80 backdrop-blur-sm">
            <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text transition-colors duration-300">
              نتائج البحث عن "{search}" ({products.length} منتج)
            </h2>
          </div>
        )}

        {category && selectedCategoryCard && (
          <div className="mb-6 p-4 rounded-xl border border-pink-200 bg-pink-100 dark:bg-dark-surface/80 dark:border-dark-border">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{selectedCategoryCard.title}</h2>
            {selectedCategoryCard.description && (
              <p className="text-sm text-gray-600 dark:text-dark-textSecondary mt-1">{selectedCategoryCard.description}</p>
            )}
          </div>
        )}

        {shouldShowProducts && (
          <>
            {products.length === 0 ? (
              <div className="text-center py-12 p-6 rounded-xl bg-white/80 dark:bg-dark-surface/80 backdrop-blur-sm">
                <p className="text-gray-500 dark:text-dark-textSecondary text-lg transition-colors duration-300">لم يتم العثور على منتجات.</p>
              </div>
            ) : (
              <div className={`grid gap-6 ${
                viewMode === 'grid'
                  ? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4'
                  : 'grid-cols-1'
              }`}>
                {products.map((product) => (
                  <ProductCard key={product._id} product={product} viewMode={viewMode} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HomePage;

